import { type ReactElement } from 'react';
import type React from 'react';

import { type Event, EventTypeEnum, ParticipantTypeEnum } from '@clubspark-node/clubspark-tennis-types';

import { type LevelConfiguration } from 'atoms/tournament';
import {
  RatingTypeEnum,
  ScaleTypeEnum,
} from 'components/panels/eventsSplitDraws/selectedEvent/rankingsAndRatings/types';
import HelpTooltip from 'components/tooltip/HelpTooltip';
import WTNTypography from 'components/typography/wtn/WTNTypography';
import { type ScaleItem } from 'typedefs/store/tmxTypes';
import { roundToOneDecimal, sum } from 'utils/math';

import {
  type ParticipantScaling,
  type ScalingOptions,
  type ScalingPurpose,
  type ScalingTypeOptions,
  type SubScalingType,
} from '.';
import Scaling from './Scaling';

const scaleName = RatingTypeEnum.WTN;
const scaleType = ScaleTypeEnum.RATING;
const sortDirection = 'inverted';
const emptyValue = Infinity;
const lowestValue = 40;
const defaultOptions: WtnScalingOptions = {
  confidenceBands: [
    { min: 0, color: '#d0d3db', description: 'Low degree' },
    { min: 70, color: '#003a70', description: 'High degree' },
  ],
};

export interface WTNScaleItem extends ScaleItem {
  scaleValue: {
    eventSubType: EventTypeEnum;
    confidence: number;
    wtnRating: number;
    gameZoneLower: number;
    gameZoneUpper: number;
    isRanked: boolean;
  };
}

export type WtnConfidenceBand = { min: number; color: string; description: string };
export interface WtnScalingOptions extends ScalingTypeOptions {
  confidenceBands: WtnConfidenceBand[];
  personIdAsNationalId?: boolean;
}

export const getRatingValue = (scaleItem: WTNScaleItem): number => scaleItem?.scaleValue?.wtnRating ?? emptyValue;
export const getRatingConfidence = (scaleItem: WTNScaleItem): number => scaleItem?.scaleValue?.confidence ?? 0;

class WTNScaling extends Scaling {
  static scaleName = scaleName;
  static scaleType = scaleType;
  static sortDirection: 'normal' | 'inverted' = sortDirection;
  static defaultScaleValue = emptyValue;

  public static scaleSortMethod = (
    purpose: ScalingPurpose,
    a: ParticipantScaling<WTNScaleItem>,
    b: ParticipantScaling<WTNScaleItem>,
    scalingTypeOptions: WtnScalingOptions = defaultOptions,
  ): number =>
    this.getValueCount(b) - this.getValueCount(a) ||
    this.getConfidenceBand(a, scalingTypeOptions) - this.getConfidenceBand(b, scalingTypeOptions) ||
    this.getValue(a) - this.getValue(b) ||
    0.5 - Math.random();

  public static getDisplay(
    participantScaling: ParticipantScaling<WTNScaleItem>,
    scalingTypeOptions: WtnScalingOptions = defaultOptions,
  ): ReactElement {
    const values =
      participantScaling.participant?.participantType === ParticipantTypeEnum.PAIR
        ? this.getIndividualParticipantScalings(participantScaling).map(this.getValue.bind(this))
        : [this.getValue(participantScaling)];
    const value = !values.filter(Number.isFinite).length ? emptyValue : sum(values);
    const confidence = this.getConfidence(participantScaling);
    const { color, description } = [...scalingTypeOptions.confidenceBands]
      .sort((a, b) => b.min - a.min)
      .find((band) => confidence >= band.min);
    return (
      <>
        <WTNTypography checkmarkColor={color} confidenceDescription={description} rating={value} />
        {participantScaling.participant?.participantType === ParticipantTypeEnum.PAIR && (
          <HelpTooltip
            arrow
            placement="right"
            title={values
              .map<React.ReactNode>((value, index) => <WTNTypography key={`${index}`} rating={value} />)
              .reduce((prev, curr) => [prev, prev && ' / ', curr], undefined)}
          />
        )}
      </>
    );
  }

  public static getText(
    participantScaling: ParticipantScaling<WTNScaleItem>,
    scalingTypeOptions: WtnScalingOptions = defaultOptions,
  ): string {
    const values =
      participantScaling.participant?.participantType === ParticipantTypeEnum.PAIR
        ? this.getIndividualParticipantScalings(participantScaling).map(this.getValue.bind(this))
        : [this.getValue(participantScaling)];
    const value = !values.filter(Number.isFinite).length ? emptyValue : sum(values.map(roundToOneDecimal));
    const confidence = this.getConfidence(participantScaling);
    const { description } = [...scalingTypeOptions.confidenceBands]
      .sort((a, b) => b.min - a.min)
      .find((band) => confidence >= band.min);
    return Number.isFinite(value)
      ? `${roundToOneDecimal(value).toFixed(1)} ${description}${
          participantScaling.participant?.participantType === ParticipantTypeEnum.PAIR
            ? ` (${this.getIndividualParticipantScalings(participantScaling)
                .map((individualParticipantScaling) => this.getText(individualParticipantScaling, scalingTypeOptions))
                .join(' / ')})`
            : ''
        }`
      : '-';
  }

  public static getValue({ participant, scaleItems }: ParticipantScaling<WTNScaleItem>): number {
    const values = scaleItems.map(getRatingValue);
    if (!values.some(Number.isFinite)) return emptyValue;
    return participant?.participantType === ParticipantTypeEnum.PAIR
      ? values.filter(Number.isFinite).reduce((total, rating) => total + rating, 0)
      : values.reduce((total, rating) => total + (Number.isFinite(rating) ? rating : lowestValue), 0) / values.length;
  }

  public static getValueCount = (participantScaling) => {
    const values =
      participantScaling.participant?.participantType === ParticipantTypeEnum.PAIR
        ? this.getIndividualParticipantScalings(participantScaling).map(this.getValue.bind(this))
        : [this.getValue(participantScaling)];
    return values.reduce((total, next) => total + (Number.isFinite(next) ? 1 : 0), 0);
  };

  public static getConfidence = ({ scaleItems }: ParticipantScaling<WTNScaleItem>) =>
    Math.min(...scaleItems.map(getRatingConfidence).filter(Number.isFinite));

  public static getConfidenceBand = (participantScaling, scalingTypeOptions: WtnScalingOptions) =>
    [...scalingTypeOptions.confidenceBands]
      .sort((a, b) => b.min - a.min)
      .findIndex((band) => this.getConfidence(participantScaling) >= band.min);

  public static getIndividualParticipantScalings = (
    participantScaling: ParticipantScaling<WTNScaleItem>,
  ): ParticipantScaling<WTNScaleItem>[] =>
    participantScaling.scaleItems.map((scaleItem) => ({
      ...participantScaling,
      participant: { ...participantScaling.participant, participantType: ParticipantTypeEnum.INDIVIDUAL },
      scaleItems: [scaleItem],
    }));

  static getIsAvailable(event: Event, levelConfig?: Partial<LevelConfiguration>) {
    return levelConfig?.wtnRankingsEnabled !== false;
  }

  public static getDisplayName = (options: ScalingOptions): string =>
    options?.event?.eventType === EventTypeEnum.DOUBLES ? 'WTN Doubles' : 'WTN Singles';

  public static getSubScalings = (): SubScalingType[] => ['WTN Singles', 'WTN Doubles'];
}

export default WTNScaling;
